import React, { useEffect, useState, useRef } from 'react'
import styled, { css } from 'styled-components'
import { motion } from 'framer-motion'

import Container from './container'
import Title from './title'
import { Box } from './UIKit/system'
import ReactSwiper from './react-swiper'
import mq from '../utils/mq'
import ProductPreview from './product-preview'
import IconButton, { type IconButtonProps } from './icon-button'

const StyledRelatedProducts = styled(Box)`
  overflow-x: hidden;
`

const StyledHeader = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 50%;
  margin-bottom: 46px;
  gap: 30px;

  ${mq.mobile(css`
    margin-bottom: 32px;
  `)}
`

const SwiperButtons = styled.div`
  align-content: start;
  display: grid;
  grid-auto-flow: column;
  gap: 18px;
  justify-content: end;
`

const StyledProductPreview = styled(ProductPreview)`
  height: 420px;
  margin-right: 70px !important;
  margin-bottom: 19px;
  width: calc(50% - 19px);

  ${mq.desktopSmall(css`
    width: 100%;
    max-width: 298px;
    height: 218px;
    padding-right: 70px !important;
  `)};
`

const SliderContainer = styled(motion.div)`
  position: relative;
`

interface RelatedProductsProps {
  title: string;
  products: any[];
}

function RelatedProducts({ title, products }: RelatedProductsProps) {
  const [swiperOptions, setSwiperOptions] = useState<ReturnType<typeof createSwiperOptions> | null>(null)
  const [slidePos, setSlidePos] = useState(1)
  const prevElRef = useRef<HTMLButtonElement>(null)
  const nextElRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setSwiperOptions(createSwiperOptions())
  }, [])

  function handleNext() {
    if (slidePos !== products.length) {
      setSlidePos(slidePos + 1)
    } else {
      setSlidePos(1)
    }
  }

  function handlePrev() {
    setSlidePos(slidePos - 1)
  }

  function createSwiperOptions() {
    return {
      slidesPerView: 'auto',
      spaceBetween: 20,
      autoHeight: true,
      navigation: {
        prevEl: prevElRef.current,
        nextEl: nextElRef.current
      },
      breakpoints: {
        1100: {
          slidesPerView: 'auto',
          autoHeight: true
        },
        768: {
          spaceBetween: 10
        }
      }
    }
  }

  const sliderButton = (
    iconName: IconButtonProps['icon']['name'],
    buttonRef: React.RefObject<HTMLButtonElement | null>,
    onClick: IconButtonProps['onClick']
  ) => (
    <IconButton
      icon={{ name: iconName, strokeColor: "black" }}
      ref={buttonRef}
      onClick={onClick}
      light
    />
  )

  return (
    <StyledRelatedProducts pt={80} pb={68}>
      <Container overflow="hidden">
        <StyledHeader>
          <Title fontSizeSmall alignSelf="center">
            {title}
          </Title>
          <SwiperButtons>
            {sliderButton("chevronLeft2", prevElRef, handlePrev)}
            {sliderButton("chevronRight2", nextElRef, handleNext)}
          </SwiperButtons>
        </StyledHeader>
        <SliderContainer>
          {products && (
            <ReactSwiper options={swiperOptions}>
              {products.map((item, index) => {
                return (
                  <ReactSwiper.Item key={index}>
                    <StyledProductPreview {...item} altStyling />
                  </ReactSwiper.Item>
                )
              })}
            </ReactSwiper>
          )}
        </SliderContainer>
      </Container>
    </StyledRelatedProducts>
  )
}

export default RelatedProducts
